import { beebeeAjaxPostJson } from 'beebee-base/lib/utility/ajax';
import { normalizeCurrency, clearAttribute, currency2Number } from 'beebee-base/lib/utility';
import { set } from '@ember/object';
import getConfig from 'beebee-base/lib/get-config';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util — shared Ember Data helper functions consumed by this
 *              addon's model files (src/models/*.js); NOT a Glimmer component
 *              and NOT itself the model registry (the models live in src/models/).
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      beebee-base/lib/utility/ajax (beebeeAjaxPostJson),
 *              beebee-base/lib/utility (clearAttribute, currency2Number,
 *              normalizeCurrency), beebee-base/lib/get-config, @ember/object (set)
 *   siblings:  -
 *   tests:     tests/unit/lib/utility-test.js
 *   gotchas:   in _setAttributes, currency2Number must run ONLY on the
 *              currency-typed custom field's numeric attribute — applying it to
 *              every eachAttribute() entry (including `value`, an @attr('string'))
 *              silently replaced stored text with 0 on the next save.
 *              _setAttributes serves four relations but only custom_fields owns an
 *              organization_custom_field: pass withCustomFieldId ONLY for that one,
 *              and never let a custom_fields entry travel with a null FK (the SP
 *              would 23502 the whole batch insert, or null out a stored row's FK).
 *
 * Shared helpers imported by several beebee-base-model model files: custom-field
 * label formatting (_createLabel), the generic saveAll payload builder used by
 * contact/project/event/etc. models (_saveAll), custom-field/address write
 * batching (_setAttributes), sorted custom-field lists, and a safe reload guard.
 */
function _createLabel(model, cSubLabel, session, intl, currency) {
  const timeZone = session?.me?.time_zone || 'Europe/Rome';
  const defaultCurrency = 'EUR';
  const formattedCurrency = normalizeCurrency(currency || session?.option_field?.intl?.currency || defaultCurrency);
  const formattedValues = cSubLabel.map(custom => {
    // A token that names the label-composition getters themselves (`label` /
    // `sub_label`) re-enters the very getter that called _createLabel, so the
    // two recurse into each other until the stack overflows and the whole page
    // dies at render (Sentry EMBER-BOOKING-VN: a tenant configured a
    // self-referential contact label, RangeError while `me.label` rendered on
    // /booking/guide_personal_data). Such a token has no standalone value here,
    // so drop it and let the remaining tokens compose the label.
    if (custom.field === 'label' || custom.field === 'sub_label') {
      return null;
    }
    const fieldValue = model[custom.field];
    if (custom.type === 'date') {
      const dateObject = fieldValue ? new Date(fieldValue) : null;
      return dateObject && !isNaN(dateObject.getTime()) ? intl.formatDate(dateObject, {
        timeZone
      }) : null;
    } else if (custom.type === 'currency') {
      return typeof fieldValue === 'number' && !isNaN(fieldValue) ? intl.formatNumber(fieldValue, {
        style: 'currency',
        currency: formattedCurrency
      }) : null;
    } else {
      return fieldValue;
    }
  });
  return formattedValues.filter(v => v != null).join(' ');
}

/**
 * Collects one relationship of `model` into `array` as save-all entries.
 *
 * `withCustomFieldId` exists because this collector serves FOUR relations
 * (custom_fields, addresses, contact_consensis, project_consensis) but only
 * `custom_field` records declare an `organization_custom_field` belongsTo. The
 * other three models have plain attributes only, so the key used to travel as a
 * constant `null` on every address and consent entry of every save-all payload.
 * update_address_all never reads it, so it was inert noise — but it made three
 * relations look like custom-field rows on the wire.
 */
async function _setAttributes(model, field, array, {
  withCustomFieldId = false
} = {}) {
  if (!model || typeof model.get !== 'function') {
    console.warn('_setAttributes: model non valido');
    return;
  }
  if (!field || typeof field !== 'string') {
    console.warn('_setAttributes: field non valido');
    return;
  }
  if (!Array.isArray(array)) {
    console.warn('_setAttributes: array non è un array valido');
    return;
  }
  let fieldArray;
  try {
    fieldArray = await model.get(field);
  } catch (err) {
    console.error(`_setAttributes: errore nel recupero di ${field}`, err);
    return;
  }
  if (!fieldArray || typeof fieldArray.forEach !== 'function') {
    console.warn(`_setAttributes: ${field} non è un array o non è iterabile`);
    return;
  }
  fieldArray.forEach(item => {
    if (!item || typeof item.getProperties !== 'function') {
      console.warn('_setAttributes: elemento non valido', item);
      return;
    }
    const props = item.getProperties('id', 'organization_custom_field');
    const id = props.id ?? null;
    const organization_custom_field_id = props.organization_custom_field?.id ?? null;

    // A custom_field with no organization_custom_field cannot be persisted, and
    // sending it anyway does damage in BOTH branches of update_custom_field_all:
    //   - id null → the INSERT branch writes organization_custom_field_id null.
    //     That column is NOT NULL, so the statement raises 23502 (surfaced by the
    //     API as a 422). The SP builds ONE multi-VALUES insert for every new
    //     custom field of the save, so the whole batch dies with it: the user
    //     loses every field they filled, not only the broken one.
    //   - id set  → the UPDATE branch runs `organization_custom_field_id = null`
    //     against a stored row, orphaning a real custom field from its definition.
    // Omitting the key instead of skipping the entry would change nothing: in
    // plpgsql `json->'missing_key'` is SQL NULL and `NULL <> 'null'` is NULL, so
    // the SP's CASE takes the same ELSE branch and still writes null.
    // Dropping the entry costs no user input either — edit-custom-field keys every
    // render branch off organization_custom_field.data_type, so a record without
    // one has no editor and can hold nothing the user typed. Warn rather than drop
    // in silence: this means the OCF was missing from the store when the record
    // was created (see the peekRecord callsites that build custom_field records).
    // `== null` covers null and undefined; '' is checked too because the SP casts
    // whatever is not the literal `null` to bigint, and `''::bigint` errors out.
    if (withCustomFieldId && (organization_custom_field_id == null || organization_custom_field_id === '')) {
      console.warn(`_setAttributes: ${field} entry skipped — no organization_custom_field (record id: ${id})`);
      return;
    }
    let attributes = null;
    if (item.hasDirtyAttributes) {
      attributes = {};
      if (typeof item.eachAttribute === 'function') {
        const isCurrency = item.get('organization_custom_field.data_type') === 'currency';
        item.eachAttribute(name => {
          const value = clearAttribute(item.get(name));

          // The currency conversion belongs to the NUMERIC attribute only. It used
          // to key off the field's data_type alone and then run EVERY attribute
          // through currency2Number — including `value`, which is @attr('string').
          // For a currency-typed custom field that meant
          // currency2Number('<p>text</p>') → parseFloat(...) || 0 → 0, so stored
          // text was silently replaced by 0 on the next save. (booleans came back
          // untouched, so `value` was the only casualty.)
          attributes[name] = isCurrency && name === 'number_value' ? currency2Number(value) : value;
        });
      }
    }
    const entry = {
      id,
      attributes
    };
    if (withCustomFieldId) {
      entry.organization_custom_field_id = organization_custom_field_id;
    }
    array.push(entry);
  });
}
function _safeReload(model) {
  if (model.isSaving || model.isLoading || model.isDeleted) {
    return model;
  }
  return model.reload();
}
function _sortedCustomFields(custom_fields, isDeleted) {
  if (!isDeleted && Array.isArray(custom_fields)) {
    return custom_fields.slice().sort((a, b) => {
      const ra = parseInt(a.organization_custom_field?.rank, 10);
      const rb = parseInt(b.organization_custom_field?.rank, 10);
      return (Number.isNaN(ra) ? Number.MAX_SAFE_INTEGER : ra) - (Number.isNaN(rb) ? Number.MAX_SAFE_INTEGER : rb);
    });
  } else {
    return [];
  }
}
async function _saveAll(model, session, saveAllRoute) {
  const mapById = async relation => {
    const rel = await model[relation];
    return rel?.map(item => item.id) || [];
  };
  const data = {
    id: model.id,
    attributes: {},
    relationships: {
      contact_state: model.contact_state?.id,
      product_state: model.product_state?.id,
      project_state: model.project_state?.id,
      event_state: model.event_state?.id,
      workhour_state: model.workhour_state?.id,
      custom_fields: [],
      contacts: await mapById('contacts'),
      related_contacts: await mapById('related_contacts'),
      products: await mapById('products'),
      related_products: await mapById('related_products'),
      projects: await mapById('projects'),
      related_projects: await mapById('related_projects'),
      events: await mapById('events'),
      related_events: await mapById('related_events'),
      payments: await mapById('payments'),
      workhours: await mapById('workhours'),
      related_workhours: await mapById('related_workhours'),
      addresses: [],
      expenses: await mapById('expenses'),
      invoices: await mapById('invoices'),
      reminders: await mapById('reminders'),
      todolists: await mapById('todolists'),
      contact_consensis: [],
      project_consensis: []
    }
  };
  model.eachAttribute(function (name, meta) {
    let trimVal = clearAttribute(model.get(name));
    if (meta.options?.currency) {
      trimVal = currency2Number(model.get(name));
    }
    data.attributes[name] = trimVal;
    set(model, name, trimVal);
  });

  // custom_fields is the only relation whose records carry an
  // organization_custom_field; the others must not emit that key at all.
  await _setAttributes(model, 'custom_fields', data.relationships.custom_fields, {
    withCustomFieldId: true
  });
  await _setAttributes(model, 'addresses', data.relationships.addresses);
  for (const relation of ['contact_consensis', 'project_consensis']) {
    if (Array.isArray(data.relationships[relation])) {
      await _setAttributes(model, relation, data.relationships[relation]);
    }
  }
  return beebeeAjaxPostJson(`${getConfig(model).APP.urlApi}/${saveAllRoute}`, session.token, data);
}
function applyStyle(text = '', styleName = null) {
  // Ember Data string attrs are null when absent; String(null) would yield
  // the literal "null" shown in UI labels, so treat null/undefined as ''.
  if (text === null || text === undefined) {
    return '';
  }
  if (typeof text !== 'string') {
    text = String(text);
  }
  if (styleName === 'upperCase') {
    return text.toUpperCase();
  } else if (styleName === 'capitalize') {
    return text.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  } else if (styleName === 'lowerCase') {
    return text.toLowerCase();
  }
  return text;
}

export { _createLabel, _safeReload, _saveAll, _sortedCustomFields, applyStyle };
//# sourceMappingURL=utility.js.map
